/**
 * DataTable.tsx
 *
 * The shared table, used by 82 pages. Below `md` it renders the same data as stacked
 * cards instead of a table, which is what converts all 82 without editing any of them.
 *
 * ── Card mode ───────────────────────────────────────────────────────────────────
 * On by default below `md`, and additive: a caller that declares nothing gets a
 * reasonable card, and on-screen behaviour at 768px and above is untouched. It is a JS
 * branch rather than two trees behind CSS for two reasons — cells are per-row render
 * functions, so mounting both would mean roughly 1,200 extra render calls on a
 * twelve-column fifty-row page; and a printed page lays out near 816px, so a CSS
 * branch would put cards on paper.
 *
 * Column hints that have no meaning without table cells — `width`, `align`,
 * `tableFixed`, `truncate` — are **ignored** in card mode rather than approximated.
 * That is deliberate, not an oversight.
 *
 * Empty cells are skipped, which is what makes the result readable: a twelve-column
 * table lands at four to six fields per card rather than six blank labels. `0` and
 * `false`-rendering nodes are not treated as empty; a zero count is information.
 */
import React, { useState, useMemo, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp, ChevronDown, ChevronsUpDown, MoreHorizontal } from 'lucide-react';
import { cn, formatCount } from '@/lib/utils';
import { useIsBelow, type BreakpointOrNever } from '@/hooks/useBreakpoint';
import { Checkbox } from './Checkbox';
import { EmptyState } from './EmptyState';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * What a column becomes on a card.
 *
 * `title` and `subtitle` are taken once each, in column order. Everything else is a
 * labelled field, and `hidden` drops the column from the card without affecting the
 * table.
 */
export type DataTableColumnRole = 'title' | 'subtitle' | 'field' | 'hidden';

export interface DataTableColumn<T> {
  key: keyof T | string;
  header: React.ReactNode;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
  align?: 'left' | 'center' | 'right';
  /** Ellipsize overflow and prevent the column from growing to fit content. */
  truncate?: boolean;
  /**
   * Card-mode placement. When no column in the set declares one, the first column
   * becomes the title and the rest become fields.
   */
  role?: DataTableColumnRole;
  /**
   * Field label to use on a card. Needed when `header` is an icon or a node — a card
   * field labelled with a bare icon reads as nothing.
   */
  label?: string;
}

export interface DataTableAction<T> {
  label: string;
  onClick: (row: T) => void;
  disabled?: (row: T) => boolean;
  hidden?: (row: T) => boolean;
  variant?: 'default' | 'danger';
}

export interface DataTableProps<T> {
  data: T[];
  columns: DataTableColumn<T>[];
  keyField: keyof T;
  /** Current page (1-indexed) */
  page?: number;
  pageSize?: number;
  total?: number;
  hasNext?: boolean;
  onPageChange?: (page: number) => void;
  /** When set (with onPageSizeChange), renders a rows-per-page select in the footer. */
  pageSizeOptions?: number[];
  onPageSizeChange?: (pageSize: number) => void;
  onRowClick?: (row: T) => void;
  /** Fired once when the pointer enters a row. Useful for hover prefetching. */
  onRowMouseEnter?: (row: T) => void;
  rowClassName?: (row: T) => string | undefined;
  rowActions?: DataTableAction<T>[];
  selectable?: boolean;
  /**
   * Per-row veto on selection. A row that fails it renders a disabled checkbox
   * and is left out of "select all", so a bulk action can never be handed a row
   * it must not touch. Defaults to every row being selectable.
   */
  isRowSelectable?: (row: T) => boolean;
  selectedIds?: Set<string | number>;
  onSelectionChange?: (ids: Set<string | number>) => void;
  loading?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  className?: string;
  /** Use table-fixed layout so column width hints are enforced. */
  tableFixed?: boolean;
  manualSort?: boolean;
  sortKey?: string | null;
  sortDir?: 'asc' | 'desc';
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void;
  /**
   * Width below which rows render as cards. `'never'` opts a page out and keeps the
   * table at every width — for a page whose columns only make sense side by side.
   */
  cardBreakpoint?: BreakpointOrNever;
  /**
   * Replace a card's body. The wrapper still supplies selection, row actions and row
   * click handling, so an escape hatch does not cost those.
   */
  renderCard?: (row: T) => React.ReactNode;
}

// ─── Card helpers ─────────────────────────────────────────────────────────────

/**
 * Whether a cell has nothing worth a line on a card.
 *
 * `0` is not empty — a zero count is information — and neither is an element, because
 * there is no way to see inside one.
 */
function isEmptyCell(value: React.ReactNode): boolean {
  if (value == null || value === false || value === '') return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0 || value.every(isEmptyCell);
  return false;
}

// ─── Skeleton row ─────────────────────────────────────────────────────────────

function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr className="border-b border-slate-100">
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-slate-200 rounded animate-pulse" />
        </td>
      ))}
    </tr>
  );
}

// ─── Row action dropdown ──────────────────────────────────────────────────────

/** Menu width, matching the `w-44` on the panel. Fallback when nothing is measurable. */
const MENU_WIDTH = 176;
/** Keeps the menu off the viewport edge on both axes. */
const MENU_GUTTER = 8;
/** Gap between the trigger and the menu when it opens downward. */
const MENU_GAP = 4;

function RowActionsMenu<T>({ row, actions }: { row: T; actions: DataTableAction<T>[] }) {
  const [open, setOpen] = useState(false);
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const trigger = btnRef.current.getBoundingClientRect();

    // Measured, not estimated. A portal's children are committed before layout
    // effects run, so the menu is already in the DOM and its real height is
    // readable here. The old 200px estimate flipped short menus upward for no
    // reason: a two-item menu is closer to 72px.
    const menu = menuRef.current?.getBoundingClientRect();
    const menuHeight = menu?.height ?? 0;
    // `||` not `??`: an unlaid-out menu measures 0 and must fall back to w-44.
    const menuWidth = menu?.width || MENU_WIDTH;

    // Right-aligned to the trigger as before, then clamped into the viewport.
    // Unclamped, a trigger inside the body's horizontal scroller opens the menu
    // off-screen, and a trigger nearer the left edge than the menu is wide opens
    // it at a negative offset.
    const maxLeft = Math.max(MENU_GUTTER, window.innerWidth - menuWidth - MENU_GUTTER);
    const left = Math.min(Math.max(MENU_GUTTER, trigger.right - menuWidth), maxLeft);

    // Below the trigger unless the measured menu does not fit there. The flip
    // anchor is carried over unchanged — the menu's bottom lines up with the
    // trigger's bottom — so a menu that always had to flip lands where it does
    // today, and only the clamp can move it.
    const fitsBelow = trigger.bottom + MENU_GAP + menuHeight <= window.innerHeight - MENU_GUTTER;
    const desiredTop = fitsBelow
      ? trigger.bottom + MENU_GAP
      : trigger.top - menuHeight + trigger.height;
    const maxTop = Math.max(MENU_GUTTER, window.innerHeight - menuHeight - MENU_GUTTER);
    const top = Math.min(Math.max(MENU_GUTTER, desiredTop), maxTop);

    setPos({ top, left });
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    // `pointerdown`, not `mousedown`: iOS Safari dispatches no mouse event for a
    // tap on a non-interactive element, which left the menu undismissable on a
    // phone. Mouse input still produces `pointerdown`, so desktop is unchanged.
    function handlePointerDown(e: Event) {
      if (
        btnRef.current && !btnRef.current.contains(e.target as Node) &&
        menuRef.current && !menuRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    function handleScroll() { setOpen(false); }
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [open]);

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="inline-flex items-center justify-center h-7 w-7 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label="Row actions"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          className="fixed z-50 w-44 rounded-md border border-slate-200 bg-white shadow-lg py-1"
          style={{ top: pos.top, left: pos.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {actions.map((action) => {
            if (action.hidden?.(row)) return null;
            const isDisabled = action.disabled?.(row) ?? false;
            return (
              <button
                key={action.label}
                type="button"
                disabled={isDisabled}
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                  action.onClick(row);
                }}
                className={cn(
                  'w-full text-left px-3 py-1.5 text-sm',
                  'disabled:cursor-not-allowed disabled:opacity-40',
                  action.variant === 'danger'
                    ? 'text-red-600 hover:bg-red-50'
                    : 'text-slate-700 hover:bg-slate-50',
                )}
              >
                {action.label}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function DataTable<T extends object>({
  data,
  columns,
  keyField,
  page,
  pageSize,
  total,
  hasNext = false,
  onPageChange,
  pageSizeOptions,
  onPageSizeChange,
  onRowClick,
  onRowMouseEnter,
  rowClassName,
  rowActions,
  selectable = false,
  isRowSelectable,
  selectedIds,
  onSelectionChange,
  loading = false,
  emptyTitle = 'No results',
  emptyDescription = 'No records match your current filters.',
  className,
  tableFixed = false,
  manualSort = false,
  sortKey: controlledSortKey,
  sortDir: controlledSortDir,
  onSortChange,
  cardBreakpoint = 'md',
  renderCard,
}: DataTableProps<T>) {
  const asCards = useIsBelow(cardBreakpoint);
  // Guard: ensure data is always an array even if caller passes null/undefined/object
  if (!Array.isArray(data)) data = [] as unknown as T[];
  const [uncontrolledSortKey, setUncontrolledSortKey] = useState<string | null>(null);
  const [uncontrolledSortDir, setUncontrolledSortDir] = useState<'asc' | 'desc'>('asc');
  const sortKey = manualSort ? (controlledSortKey ?? null) : uncontrolledSortKey;
  const sortDir = manualSort ? (controlledSortDir ?? 'asc') : uncontrolledSortDir;

  // ─── Sorting ────────────────────────────────────────────────────────────

  const handleSort = useCallback(
    (key: string) => {
      const nextDir = sortKey === key
        ? (sortDir === 'asc' ? 'desc' : 'asc')
        : 'asc';

      if (manualSort) {
        onSortChange?.(key, nextDir);
        return;
      }

      if (sortKey === key) {
        setUncontrolledSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setUncontrolledSortKey(key);
        setUncontrolledSortDir('asc');
      }
    },
    [manualSort, onSortChange, sortDir, sortKey],
  );

  const sortedData = useMemo(() => {
    if (manualSort) return data;
    if (!sortKey) return data;
    const col = columns.find((c) => c.key === sortKey);
    if (!col?.sortable || col.render) return data;
    return [...data].sort((a, b) => {
      const av = a[sortKey as keyof T];
      const bv = b[sortKey as keyof T];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, columns, manualSort, sortKey, sortDir]);

  // ─── Selection ──────────────────────────────────────────────────────────

  const canSelectRow = useCallback(
    (row: T) => isRowSelectable?.(row) ?? true,
    [isRowSelectable],
  );

  // Only selectable rows: "select all" must not sweep up vetoed rows, and the
  // header checkbox has to be able to reach "all selected" while they exist.
  const allIds = useMemo(
    () => data.filter(canSelectRow).map((row) => row[keyField] as string | number),
    [data, keyField, canSelectRow],
  );

  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds?.has(id));
  const someSelected = !allSelected && allIds.some((id) => selectedIds?.has(id));

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (allSelected) {
      const next = new Set(selectedIds);
      allIds.forEach((id) => next.delete(id));
      onSelectionChange(next);
    } else {
      const next = new Set(selectedIds);
      allIds.forEach((id) => next.add(id));
      onSelectionChange(next);
    }
  }, [allSelected, allIds, selectedIds, onSelectionChange]);

  const handleSelectRow = useCallback(
    (id: string | number) => {
      if (!onSelectionChange || !selectedIds) return;
      const next = new Set(selectedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      onSelectionChange(next);
    },
    [selectedIds, onSelectionChange],
  );

  // ─── Column count (for skeleton) ─────────────────────────────────────────

  const totalCols =
    columns.length + (selectable ? 1 : 0) + (rowActions && rowActions.length > 0 ? 1 : 0);

  // ─── Card mode ───────────────────────────────────────────────────────────

  /**
   * Column order is preserved; only the placement is resolved. The implicit title
   * applies solely when nothing declares a role, so a page that declares any role gets
   * exactly what it asked for and no surprise title.
   */
  const cardColumns = useMemo(() => {
    const declared = columns.some((col) => col.role);
    return columns.map((col, index) => ({
      col,
      role: col.role ?? (!declared && index === 0 ? 'title' : 'field'),
    }));
  }, [columns]);

  function RowCard({ row }: { row: T }) {
    const id = row[keyField] as string | number;
    const isSelected = selectedIds?.has(id) ?? false;

    let title: React.ReactNode = null;
    let subtitle: React.ReactNode = null;
    const fields: { key: string; label: React.ReactNode; value: React.ReactNode }[] = [];

    for (const { col, role } of cardColumns) {
      if (role === 'hidden') continue;

      // Same expression the table cell uses, so a card never shows a different value.
      const value = col.render ? col.render(row) : (row[col.key as keyof T] as React.ReactNode);
      if (isEmptyCell(value)) continue;

      if (role === 'title' && title === null) {
        title = value;
      } else if (role === 'subtitle' && subtitle === null) {
        subtitle = value;
      } else {
        fields.push({ key: String(col.key), label: col.label ?? col.header, value });
      }
    }

    return (
      <div
        onClick={onRowClick ? () => onRowClick(row) : undefined}
        onMouseEnter={onRowMouseEnter ? () => onRowMouseEnter(row) : undefined}
        className={cn(
          'flex items-start gap-3 px-4 py-3 transition-colors',
          onRowClick && 'cursor-pointer hover:bg-slate-50',
          isSelected && 'bg-blue-50',
          rowClassName?.(row),
        )}
      >
        {selectable && (
          <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              disabled={!canSelectRow(row)}
              onChange={() => handleSelectRow(id)}
              aria-label={`Select row ${id}`}
            />
          </div>
        )}

        {/* min-w-0 plus break-words on every value: the one page that defends itself
            against long unbroken strings does it with a table-cell-scoped override,
            which has no cells to apply to here. */}
        <div className="min-w-0 flex-1">
          {renderCard ? (
            renderCard(row)
          ) : (
            <>
              {title !== null && (
                <div className="break-words text-sm font-medium text-slate-900">{title}</div>
              )}
              {subtitle !== null && (
                <div className="break-words text-xs text-slate-500">{subtitle}</div>
              )}

              {fields.length > 0 && (
                <dl className={cn('space-y-1', (title !== null || subtitle !== null) && 'mt-2')}>
                  {fields.map((field) => (
                    <div key={field.key} className="flex flex-wrap gap-x-2 text-xs">
                      <dt className="shrink-0 text-slate-500">{field.label}</dt>
                      <dd className="min-w-0 flex-1 break-words text-slate-700">{field.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </>
          )}
        </div>

        {rowActions && rowActions.length > 0 && (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <RowActionsMenu row={row} actions={rowActions} />
          </div>
        )}
      </div>
    );
  }

  const sortableColumns = useMemo(() => columns.filter((col) => col.sortable), [columns]);

  /**
   * Stands in for the sticky table head, which is where sorting and select-all live on
   * desktop. Rendered only when there is something for it to do — a table with no
   * sortable column and no selection has no head worth replacing.
   */
  function CardSortBar() {
    return (
      <div className="sticky top-0 z-10 flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-4 py-2">
        {selectable && (
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Checkbox
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={handleSelectAll}
              aria-label="Select all rows"
            />
            Select all
          </label>
        )}

        {sortableColumns.length > 0 && (
          <div className="ml-auto flex items-center gap-1.5">
            {/* Reuses handleSort unchanged: a new key sorts ascending, and the button
                re-sorts the current key, which is how the handler already toggles. */}
            <select
              value={sortKey ?? ''}
              onChange={(e) => handleSort(e.target.value)}
              aria-label="Sort by"
              className="min-h-[44px] rounded border border-slate-200 bg-white px-2 text-sm text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="" disabled>
                Sort by…
              </option>
              {sortableColumns.map((col) => (
                <option key={String(col.key)} value={String(col.key)}>
                  {col.label ?? (typeof col.header === 'string' ? col.header : String(col.key))}
                </option>
              ))}
            </select>

            <button
              type="button"
              disabled={!sortKey}
              onClick={() => sortKey && handleSort(sortKey)}
              aria-label={sortDir === 'asc' ? 'Sort descending' : 'Sort ascending'}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {sortDir === 'asc' ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>
        )}
      </div>
    );
  }

  /**
   * Loading and empty states are handled here as well as in the table, because the
   * table's versions are a `<tr>` and a `colSpan` cell — neither can render without a
   * table around them.
   */
  function CardBody() {
    const showSortBar =
      (sortableColumns.length > 0 || selectable) && (loading || sortedData.length > 0);

    return (
      <>
        {showSortBar && <CardSortBar />}

        {loading ? (
          <div className="divide-y divide-slate-100">
            {Array.from({ length: pageSize ?? 10 }).map((_, i) => (
              <div key={i} className="space-y-2 px-4 py-3">
                <div className="h-4 w-1/2 animate-pulse rounded bg-slate-200" />
                <div className="h-3 w-3/4 animate-pulse rounded bg-slate-200" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-slate-200" />
              </div>
            ))}
          </div>
        ) : sortedData.length === 0 ? (
          <EmptyState title={emptyTitle} description={emptyDescription} />
        ) : (
          <div className="divide-y divide-slate-100 bg-white">
            {sortedData.map((row) => (
              <RowCard key={row[keyField] as string | number} row={row} />
            ))}
          </div>
        )}
      </>
    );
  }

  // ─── Sort icon helper ────────────────────────────────────────────────────

  function SortIcon({ colKey }: { colKey: string }) {
    if (sortKey !== colKey) return <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />;
    return sortDir === 'asc' ? (
      <ChevronUp className="h-3.5 w-3.5 text-blue-600" />
    ) : (
      <ChevronDown className="h-3.5 w-3.5 text-blue-600" />
    );
  }

  const alignClass: Record<string, string> = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <div className={cn('flex h-full flex-col border-t border-slate-200', className)}>
      {/* The scroll container stays a direct child of the root in both modes: three
          consuming pages scope `overflow-x-hidden` to it with a direct-child selector. */}
      <div className="min-h-0 flex-1 overflow-auto">
        {asCards ? <CardBody /> : (
        <table className={cn('w-full text-sm', tableFixed && 'table-fixed')}>
          {/* Head */}
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
            <tr>
              {selectable && (
                <th className="w-10 px-4 py-3">
                  <Checkbox
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={handleSelectAll}
                    aria-label="Select all rows"
                  />
                </th>
              )}

              {columns.map((col) => {
                const key = String(col.key);
                return (
                  <th
                    key={key}
                    className={cn(
                      'px-4 py-3 text-xs font-semibold text-slate-600 whitespace-nowrap',
                      col.width ?? '',
                      alignClass[col.align ?? 'left'],
                      col.sortable && 'cursor-pointer select-none hover:text-slate-900',
                    )}
                    onClick={col.sortable ? () => handleSort(key) : undefined}
                  >
                    <span className="flex min-w-0 items-center gap-1">
                      {col.truncate ? (
                        <span
                          className="truncate"
                          title={typeof col.header === 'string' ? col.header : undefined}
                        >
                          {col.header}
                        </span>
                      ) : (
                        col.header
                      )}
                      {col.sortable && <SortIcon colKey={key} />}
                    </span>
                  </th>
                );
              })}

              {rowActions && rowActions.length > 0 && (
                <th className="w-10 px-1 py-3" aria-label="Actions" />
              )}
            </tr>
          </thead>

          {/* Body */}
          <tbody className="divide-y divide-slate-100 bg-white">
            {loading ? (
              Array.from({ length: pageSize ?? 10 }).map((_, i) => (
                <SkeletonRow key={i} cols={totalCols} />
              ))
            ) : sortedData.length === 0 ? (
              <tr>
                <td colSpan={totalCols}>
                  <EmptyState title={emptyTitle} description={emptyDescription} />
                </td>
              </tr>
            ) : (
              sortedData.map((row) => {
                const id = row[keyField] as string | number;
                const isSelected = selectedIds?.has(id) ?? false;

                return (
                  <tr
                    key={id}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onMouseEnter={onRowMouseEnter ? () => onRowMouseEnter(row) : undefined}
                    className={cn(
                      'transition-colors',
                      onRowClick && 'cursor-pointer hover:bg-slate-50',
                      isSelected && 'bg-blue-50',
                      rowClassName?.(row),
                    )}
                  >
                    {selectable && (
                      <td className="w-10 px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          disabled={!canSelectRow(row)}
                          onChange={() => handleSelectRow(id)}
                          aria-label={`Select row ${id}`}
                        />
                      </td>
                    )}

                    {columns.map((col) => {
                      const key = String(col.key);
                      const cellValue = col.render
                        ? col.render(row)
                        : (row[col.key as keyof T] as React.ReactNode);

                      return (
                        <td
                          key={key}
                          className={cn(
                            'px-4 py-3 text-slate-700',
                            col.width ?? '',
                            alignClass[col.align ?? 'left'],
                          )}
                        >
                          {col.truncate ? (
                            <div
                              className="truncate"
                              title={typeof cellValue === 'string' ? cellValue : undefined}
                            >
                              {cellValue}
                            </div>
                          ) : (
                            cellValue
                          )}
                        </td>
                      );
                    })}

                    {rowActions && rowActions.length > 0 && (
                      <td className="w-10 px-1 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                        <RowActionsMenu row={row} actions={rowActions} />
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        )}
      </div>

      {/*
        Pagination footer — fixed at bottom, outside scroll area.

        Restacking and the touch floor below are scoped to `asCards`, not to a `max-md:`
        class. This footer renders at every width, and at 768–1023 the desktop table is
        what is on screen — a class escape would restack its footer too.
      */}
      {(typeof page === 'number' && typeof pageSize === 'number' && onPageChange && (typeof total === 'number' || hasNext)) && (
        <div
          className={cn(
            'shrink-0 flex border-t border-slate-200 bg-slate-50 px-4 py-2',
            asCards ? 'flex-col items-stretch gap-2' : 'items-center justify-between',
          )}
        >
          <span className="text-xs text-slate-500">
            {typeof total === 'number'
              ? (total === 0
              ? 'No results'
              : `Showing ${formatCount(Math.min((page - 1) * pageSize + 1, total))}–${formatCount(Math.min(page * pageSize, total))} of ${formatCount(total)}`)
              : (data.length === 0
                ? 'No results'
                : `Showing ${formatCount((page - 1) * pageSize + 1)}–${formatCount((page - 1) * pageSize + data.length)}`)}
          </span>

          <div className={cn('flex items-center', asCards ? 'gap-2' : 'gap-1')}>
            {pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange && (
              <label
                className={cn(
                  'flex items-center gap-1.5 text-xs text-slate-500',
                  asCards ? 'shrink-0 gap-2' : 'mr-3',
                )}
              >
                Rows per page
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  disabled={loading}
                  className={cn(
                    'rounded border border-slate-200 bg-white text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40',
                    asCards ? 'min-h-[44px] px-2 text-sm' : 'px-1.5 py-1 text-xs',
                  )}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </label>
            )}
            {/* These are raw buttons rather than the shared component, so the shared
                touch-target treatment will not reach them. Their floor lives here. */}
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => onPageChange(page - 1)}
              className={cn(
                'rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed',
                asCards ? 'min-h-[44px] flex-1 px-4 text-sm' : 'px-2 py-1 text-xs',
              )}
            >
              Previous
            </button>
            <button
              type="button"
              disabled={(typeof total === 'number' ? page * pageSize >= total : !hasNext) || loading}
              onClick={() => onPageChange(page + 1)}
              className={cn(
                'rounded border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed',
                asCards ? 'min-h-[44px] flex-1 px-4 text-sm' : 'px-2 py-1 text-xs',
              )}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
